import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, PackageOpen, Tag, Truck, Zap, CreditCard, Smartphone, Landmark, Wallet, Banknote, CheckCircle2 } from "lucide-react";
import { api, fmt, apiError, fmtDateLong, fmtPerUnit, fmtQty, getSessionPin, openRazorpayCheckout } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { useAuth } from "@/context/AuthContext";
import { ShinyButton } from "@/components/ui/ShinyButton";


const PAYMENT_METHODS = [
  { id: "upi", label: "UPI", desc: "GPay, PhonePe, Paytm", icon: Smartphone },
  { id: "card", label: "Credit / Debit Card", desc: "Visa, Mastercard, RuPay", icon: CreditCard },
  { id: "netbanking", label: "Net Banking", desc: "All major Indian banks", icon: Landmark },
  { id: "wallet", label: "Wallets", desc: "Paytm, Amazon Pay", icon: Wallet },
  { id: "cod", label: "Cash on Delivery", desc: "Pay when it arrives", icon: Banknote },
];

const EMPTY_ADDRESS = { full_name: "", mobile: "", line1: "", line2: "", city: "", state: "", pin: "", country: "India" };

export default function CheckoutPage() {
  const { cart, cartLoading, clearGuestCart, refreshCart, gstEnabled } = useStore();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [contact, setContact] = useState({ name: "", email: "", mobile: "" });
  const [address, setAddress] = useState(() => {
    const sessionPin = getSessionPin();
    return sessionPin ? { ...EMPTY_ADDRESS, pin: sessionPin } : EMPTY_ADDRESS;
  });
  const [shippingMethod, setShippingMethod] = useState("standard");
  const [deliveryQuote, setDeliveryQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("upi");
  const [payGroup, setPayGroup] = useState("paynow");
  const [creditCtx, setCreditCtx] = useState(null);
  const [saveAddress, setSaveAddress] = useState(true);
  const [couponInput, setCouponInput] = useState("");
  const [coupon, setCoupon] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);
  const [placing, setPlacing] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (user) {
      const biz = user.business;
      setContact((c) => ({
        ...c,
        name: c.name || biz?.contact_person || user.name,
        email: c.email || biz?.business_email || user.email,
        mobile: c.mobile || biz?.phone || "",
      }));
      const def = user.addresses?.find((a) => a.is_default) || user.addresses?.[0];
      if (def) {
        setAddress((a) => ({ ...EMPTY_ADDRESS, ...def }));
        setContact((c) => ({ ...c, mobile: def.mobile || c.mobile }));
      }
    }
  }, [user]);

  // PRIME delivery benefit: exact fare → discount → payable from the server.
  const [primeBenefit, setPrimeBenefit] = useState(null);
  useEffect(() => {
    if (!user || !deliveryQuote?.serviceable) { setPrimeBenefit(null); return; }
    let stale = false;
    api.post("/prime/benefit-preview", {
      pin: address.pin.trim(),
      method: shippingMethod,
      merchandise: Math.max(0, (cart.subtotal || 0) - (coupon?.discount || 0)),
      coupon_applied: !!coupon,
    })
      .then(({ data }) => { if (!stale) setPrimeBenefit(data); })
      .catch(() => { if (!stale) setPrimeBenefit(null); });
    return () => { stale = true; };
  }, [user, deliveryQuote, address.pin, shippingMethod, cart.subtotal, coupon]);

  const totals = useMemo(() => {
    const items = cart.items || [];
    const subtotal = cart.subtotal || 0;
    const discount = coupon?.discount || 0;
    const shipping = deliveryQuote?.serviceable
      ? (shippingMethod === "standard" ? deliveryQuote.standard.charge : deliveryQuote.express.charge)
      : 0;
    const primeDiscount = primeBenefit?.eligible ? primeBenefit.discount : 0;
    // Per-line GST estimate — zeroed in pre-GST mode (cart.gst_enabled from server).
    let tax = 0;
    if (gstEnabled && items.length && subtotal > 0) {
      let running = 0;
      items.forEach((it, idx) => {
        const lineTotal = Math.round(it.unit_price * it.qty * 100) / 100;
        const share = idx === items.length - 1
          ? Math.round((discount - running) * 100) / 100
          : Math.round((discount * lineTotal) / subtotal * 100) / 100;
        running = Math.round((running + share) * 100) / 100;
        const taxable = Math.round((lineTotal - Math.max(0, share)) * 100) / 100;
        tax += Math.round(taxable * ((it.gst_rate ?? 18) / 100) * 100) / 100;
      });
    }
    return { subtotal, discount, shipping, primeDiscount, tax, total: Math.round((subtotal - discount + shipping - primeDiscount + tax) * 100) / 100 };
  }, [cart, coupon, shippingMethod, deliveryQuote, gstEnabled, primeBenefit]);

  // Live delivery quote: PIN → configured PAQVERO delivery zone (server-side rules).
  useEffect(() => {
    const pin = address.pin.trim();
    if (!/^[1-9][0-9]{5}$/.test(pin)) { setDeliveryQuote(null); return; }
    let stale = false;
    setQuoteLoading(true);
    const t = setTimeout(() => {
      api.post("/delivery/quote", { pin, subtotal: Math.max(0, (cart.subtotal || 0) - (coupon?.discount || 0)), method: shippingMethod })
        .then(({ data }) => { if (!stale) setDeliveryQuote(data); })
        .catch(() => { if (!stale) setDeliveryQuote(null); })
        .finally(() => { if (!stale) setQuoteLoading(false); });
    }, 400);
    return () => { stale = true; clearTimeout(t); };
  }, [address.pin, cart.subtotal, coupon, shippingMethod]);

  useEffect(() => {
    if (!user) { setCreditCtx(null); return; }
    let stale = false;
    api.get(`/credit/checkout-context?total=${totals.total}`)
      .then(({ data }) => { if (!stale) setCreditCtx(data); })
      .catch(() => { if (!stale) setCreditCtx(null); });
    return () => { stale = true; };
  }, [user, totals.total]);

  useEffect(() => {
    if (payGroup === "credit" && creditCtx && !creditCtx.eligible) setPayGroup("paynow");
  }, [creditCtx, payGroup]);

  const applyCoupon = async () => {
    if (!couponInput.trim()) return;
    if (totals.subtotal <= 0) {
      toast.error("Hold on — your cart is still loading");
      return;
    }
    setCouponLoading(true);
    try {
      const hasPrime = (cart.items || []).some((i) => i.prime_priced);
      const { data } = await api.post("/shop/coupons/validate", { code: couponInput, subtotal: totals.subtotal, has_prime: hasPrime });
      setCoupon(data);
      toast.success(`Coupon ${data.code} applied — you save ${fmt(data.discount)}`);
    } catch (e) {
      setCoupon(null);
      toast.error(apiError(e, "Invalid coupon code"));
    } finally {
      setCouponLoading(false);
    }
  };

  // Re-validate an applied coupon whenever the cart subtotal changes — the stored
  // discount must never go stale (qty edits, late cart load) or fall below min_order.
  useEffect(() => {
    if (!coupon) return;
    api.post("/shop/coupons/validate", { code: coupon.code, subtotal: cart.subtotal || 0, has_prime: (cart.items || []).some((i) => i.prime_priced) })
      .then(({ data }) => setCoupon(data))
      .catch(() => {
        setCoupon(null);
        setCouponInput("");
        toast.error(`Coupon ${coupon.code} no longer applies to this cart and was removed`);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart.subtotal]);

  const validate = () => {
    const errs = {};
    if (contact.name.trim().length < 2) errs.name = "Required";
    if (!/^\S+@\S+\.\S+$/.test(contact.email)) errs.email = "Valid email required";
    if (contact.mobile.trim().length < 8) errs.mobile = "Valid mobile required";
    for (const k of ["full_name", "line1", "city", "state", "pin"]) {
      if (!address[k] || address[k].trim().length < 2) errs[k] = "Required";
    }
    if (address.mobile.trim().length < 8) errs.addr_mobile = "Required";
    setErrors(errs);
    if (Object.keys(errs).length) {
      toast.error("Please complete the highlighted fields");
      return false;
    }
    return true;
  };

  const placeOrder = async () => {
    if (!validate()) return;
    setPlacing(true);
    try {
      const payload = {
        contact,
        address,
        shipping_method: shippingMethod,
        payment_method: payGroup === "credit" ? "credit" : paymentMethod,
        coupon_code: coupon?.code || "",
        save_address: !!user && saveAddress,
        items: user ? [] : cart.items.map((i) => ({ product_id: i.product_id, sku: i.sku, qty: i.qty })),
      };
      const { data } = await api.post("/orders/checkout", payload);
      const order = data.order;
      const rememberGuest = () => {
        if (user) return;
        const stored = JSON.parse(localStorage.getItem("packo_guest_orders") || "[]");
        stored.unshift({ id: order.id, access: order.access_token });
        localStorage.setItem("packo_guest_orders", JSON.stringify(stored.slice(0, 20)));
      };
      const finishCart = () => (user ? refreshCart() : Promise.resolve(clearGuestCart()));

      if (data.razorpay) {
        // Two-phase Razorpay: order exists but is UNPAID until the server verifies the signature.
        rememberGuest();
        try {
          const rp = await openRazorpayCheckout({
            keyId: data.razorpay.key_id,
            orderId: data.razorpay.order_id,
            amountPaise: data.razorpay.amount,
            prefill: { name: contact.name, email: contact.email, contact: contact.mobile },
            description: `PAQVERO Order ${order.order_number}`,
          });
          await api.post("/payments/razorpay/verify", {
            order_id: order.id,
            access: order.access_token || "",
            razorpay_order_id: rp.razorpay_order_id,
            razorpay_payment_id: rp.razorpay_payment_id,
            razorpay_signature: rp.razorpay_signature,
          });
          await finishCart();
          navigate(`/orders/${order.id}?confirmed=1${user ? "" : `&access=${order.access_token}`}`);
          toast.success(`Order ${order.order_number} confirmed — payment received`);
        } catch (e) {
          await finishCart();
          toast.error(
            e?.reason === "cancelled"
              ? "Payment cancelled — your order is saved and awaiting payment"
              : e?.reason === "failed"
                ? `${e.message} — your order is saved as unpaid`
                : apiError(e, "Payment couldn't be verified — your order is saved as unpaid"),
          );
          navigate(`/orders/${order.id}${user ? "" : `?access=${order.access_token}`}`);
        }
        return;
      }

      if (user) {
        await refreshCart();
        navigate(`/orders/${order.id}?confirmed=1`);
      } else {
        rememberGuest();
        clearGuestCart();
        navigate(`/orders/${order.id}?confirmed=1&access=${order.access_token}`);
      }
      toast.success(`Order ${order.order_number} confirmed`);
    } catch (e) {
      toast.error(apiError(e, "Payment couldn't be completed. Your order has not been placed."));
    } finally {
      setPlacing(false);
    }
  };

  const inputCls = (bad) =>
    `h-12 w-full rounded-xl border bg-input px-4 text-sm text-1 placeholder:text-[var(--pq-text3)] focus:outline-none ${
      bad ? "border-red-500/60" : "border-line focus:border-amber-500/60"
    }`;

  if (!cartLoading && cart.items.length === 0) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 px-6 text-center" data-testid="checkout-empty">
        <span className="flex h-16 w-16 items-center justify-center rounded-2xl border border-line bg-surface text-amber-500 shadow-card">
          <PackageOpen className="h-7 w-7" strokeWidth={1.6} />
        </span>
        <h1 className="font-display text-3xl font-semibold tracking-tight text-1">Your cart is empty.</h1>
        <p className="text-sm text-3">Add some packaging essentials before checking out.</p>
        <button onClick={() => navigate("/shop")} className="mt-2 rounded-full bg-amber-500 px-7 py-3.5 text-sm font-bold text-black hover:bg-amber-400" data-testid="checkout-empty-shop">
          Continue Shopping
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-24 pt-28" data-testid="checkout-page">
      <div className="mx-auto max-w-6xl px-6 lg:px-12">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <p className="mb-3 flex items-center gap-3 text-xs font-bold uppercase tracking-[0.25em] text-amber-500">
            <span className="h-px w-10 bg-amber-500" />
            Secure Checkout
          </p>
          <h1 className="font-display text-3xl font-semibold tracking-tight text-1 sm:text-4xl">Checkout</h1>
          {!user && (
            <p className="mt-2 text-sm text-3">
              Checking out as guest.{" "}
              <button onClick={() => navigate("/login", { state: { from: "/checkout" } })} className="font-bold text-amber-500 hover:text-amber-400" data-testid="checkout-signin-link">
                Sign in
              </button>{" "}
              to save addresses and track orders.
            </p>
          )}
        </motion.div>

        <div className="mt-10 grid gap-8 lg:grid-cols-5">
          <div className="space-y-6 lg:col-span-3">
            <section className="rounded-2xl border border-line bg-surface p-6 shadow-card" data-testid="checkout-contact">
              <div className="mb-5 flex items-center gap-3">
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-accent-soft text-xs font-bold text-amber-500">1</span>
                <h2 className="font-display text-lg font-medium tracking-tight text-1">Contact Information</h2>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <input data-testid="contact-name" value={contact.name} onChange={(e) => setContact((c) => ({ ...c, name: e.target.value }))} placeholder="Full name" className={inputCls(errors.name)} />
                  {errors.name && <p className="mt-1 text-xs text-red-400">{errors.name}</p>}
                </div>
                <div>
                  <input data-testid="contact-mobile" value={contact.mobile} onChange={(e) => setContact((c) => ({ ...c, mobile: e.target.value }))} placeholder="Mobile number" className={inputCls(errors.mobile)} />
                  {errors.mobile && <p className="mt-1 text-xs text-red-400">{errors.mobile}</p>}
                </div>
                <div className="sm:col-span-2">
                  <input data-testid="contact-email" type="email" value={contact.email} onChange={(e) => setContact((c) => ({ ...c, email: e.target.value }))} placeholder="Email address" className={inputCls(errors.email)} />
                  {errors.email && <p className="mt-1 text-xs text-red-400">{errors.email}</p>}
                </div>
              </div>
            </section>

            <section className="rounded-2xl border border-line bg-surface p-6 shadow-card" data-testid="checkout-address">
              <div className="mb-5 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-accent-soft text-xs font-bold text-amber-500">2</span>
                  <h2 className="font-display text-lg font-medium tracking-tight text-1">Delivery Address</h2>
                </div>
                {user?.addresses?.length > 0 && (
                  <div className="flex gap-2">
                    {user.addresses.map((a) => (
                      <button
                        key={a.id}
                        data-testid={`address-chip-${a.id}`}
                        onClick={() => setAddress({ ...EMPTY_ADDRESS, ...a })}
                        className="rounded-full border border-line px-3.5 py-1.5 text-[11px] font-bold text-2 transition-colors hover:border-amber-500/50 hover:text-amber-400"
                      >
                        {a.label}: {a.city}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <input data-testid="address-name" value={address.full_name} onChange={(e) => setAddress((a) => ({ ...a, full_name: e.target.value }))} placeholder="Full name" className={inputCls(errors.full_name)} />
                  {errors.full_name && <p className="mt-1 text-xs text-red-400">{errors.full_name}</p>}
                </div>
                <div>
                  <input data-testid="address-mobile" value={address.mobile} onChange={(e) => setAddress((a) => ({ ...a, mobile: e.target.value }))} placeholder="Mobile number" className={inputCls(errors.addr_mobile)} />
                  {errors.addr_mobile && <p className="mt-1 text-xs text-red-400">{errors.addr_mobile}</p>}
                </div>
                <div className="sm:col-span-2">
                  <input data-testid="address-line1" value={address.line1} onChange={(e) => setAddress((a) => ({ ...a, line1: e.target.value }))} placeholder="Address line 1 — flat, building, street" className={inputCls(errors.line1)} />
                  {errors.line1 && <p className="mt-1 text-xs text-red-400">{errors.line1}</p>}
                </div>
                <div className="sm:col-span-2">
                  <input data-testid="address-line2" value={address.line2} onChange={(e) => setAddress((a) => ({ ...a, line2: e.target.value }))} placeholder="Address line 2 — area, landmark (optional)" className={inputCls(false)} />
                </div>
                <div>
                  <input data-testid="address-city" value={address.city} onChange={(e) => setAddress((a) => ({ ...a, city: e.target.value }))} placeholder="City" className={inputCls(errors.city)} />
                  {errors.city && <p className="mt-1 text-xs text-red-400">{errors.city}</p>}
                </div>
                <div>
                  <input data-testid="address-state" value={address.state} onChange={(e) => setAddress((a) => ({ ...a, state: e.target.value }))} placeholder="State" className={inputCls(errors.state)} />
                  {errors.state && <p className="mt-1 text-xs text-red-400">{errors.state}</p>}
                </div>
                <div>
                  <input data-testid="address-pin" value={address.pin} onChange={(e) => setAddress((a) => ({ ...a, pin: e.target.value }))} placeholder="PIN code" className={inputCls(errors.pin)} />
                  {errors.pin && <p className="mt-1 text-xs text-red-400">{errors.pin}</p>}
                </div>
                <div>
                  <input data-testid="address-country" value={address.country} onChange={(e) => setAddress((a) => ({ ...a, country: e.target.value }))} placeholder="Country" className={inputCls(false)} />
                </div>
              </div>
              {user && (
                <label className="mt-4 flex cursor-pointer items-center gap-2.5 text-sm text-2">
                  <input type="checkbox" checked={saveAddress} onChange={(e) => setSaveAddress(e.target.checked)} className="h-4 w-4 accent-amber-500" data-testid="address-save-checkbox" />
                  Save this address to my account
                </label>
              )}
            </section>

            <section className="rounded-2xl border border-line bg-surface p-6 shadow-card" data-testid="checkout-delivery">
              <div className="mb-5 flex items-center gap-3">
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-accent-soft text-xs font-bold text-amber-500">3</span>
                <h2 className="font-display text-lg font-medium tracking-tight text-1">Delivery Method</h2>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                {[
                  { id: "standard", icon: Truck, label: "Standard Delivery", fee: deliveryQuote?.serviceable ? (deliveryQuote.standard.free ? "FREE" : fmt(deliveryQuote.standard.charge)) : "—", est: deliveryQuote?.standard?.estimate?.text },
                  ...(deliveryQuote && !deliveryQuote.express?.enabled ? [] : [{ id: "express", icon: Zap, label: "Express Delivery", fee: deliveryQuote?.serviceable ? fmt(deliveryQuote.express.charge) : "—", est: deliveryQuote?.express?.estimate?.text }]),
                ].map((m) => (
                  <button
                    key={m.id}
                    data-testid={`delivery-${m.id}`}
                    onClick={() => setShippingMethod(m.id)}
                    className={`rounded-xl border p-5 text-left transition-all ${
                      shippingMethod === m.id ? "border-amber-500 bg-amber-500/[0.07]" : "border-line hoverable-strong"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <m.icon className={`h-5 w-5 ${shippingMethod === m.id ? "text-amber-400" : "text-3"}`} />
                      <span className="font-display text-base font-semibold text-1">{m.fee}</span>
                    </div>
                    <p className="mt-3 text-sm font-bold text-1">{m.label}</p>
                    <p className="mt-1 text-xs text-3">Estimated delivery: {m.est || "—"}</p>
                  </button>
                ))}
              </div>

              {quoteLoading && (
                <p className="mt-3 flex items-center gap-2 text-xs text-3" data-testid="delivery-quote-loading">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> Checking delivery zone…
                </p>
              )}
              {!quoteLoading && deliveryQuote?.serviceable && (
                <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 rounded-xl border border-emerald-500/25 bg-emerald-500/[0.05] px-4 py-3 text-xs" data-testid="delivery-zone-result">
                  <span className="text-2">Delivery zone: <span className="font-bold text-1" data-testid="delivery-zone-label">{deliveryQuote.zone_label}{deliveryQuote.zone === "origin" ? " · Origin" : ""}</span>{deliveryQuote.label && <span className="font-semibold text-2" data-testid="delivery-locality"> · {deliveryQuote.label}</span>}</span>
                  <span className="text-2">Delivery: {shippingMethod === "standard" && deliveryQuote.standard.free ? <span className="font-bold text-emerald-400" data-testid="delivery-free">FREE</span> : <span className="font-bold text-1" data-testid="delivery-charge">{fmt(shippingMethod === "standard" ? deliveryQuote.standard.charge : deliveryQuote.express.charge)}</span>}</span>
                  <span className="text-2">Estimated delivery: <span className="font-bold text-1" data-testid="delivery-estimate">{shippingMethod === "standard" ? deliveryQuote.standard.estimate.text : deliveryQuote.express.estimate.text}</span></span>
                </div>
              )}
              {!quoteLoading && deliveryQuote && !deliveryQuote.serviceable && (
                <p className="mt-3 rounded-xl border border-red-500/25 bg-red-500/[0.06] px-4 py-3 text-xs font-semibold text-red-300" data-testid="delivery-unserviceable">
                  {deliveryQuote.message}
                </p>
              )}
              <p className="mt-3 text-[11px] leading-relaxed text-3">
                Free delivery on orders ₹1,000+ within eligible PAQVERO delivery zones.
              </p>
            </section>

            <section className="rounded-2xl border border-line bg-surface p-6 shadow-card" data-testid="checkout-payment">
              <div className="mb-5 flex items-center gap-3">
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-accent-soft text-xs font-bold text-amber-500">4</span>
                <h2 className="font-display text-lg font-medium tracking-tight text-1">Payment Method</h2>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <button
                  data-testid="paygroup-paynow"
                  onClick={() => setPayGroup("paynow")}
                  className={`rounded-xl border p-5 text-left transition-all ${payGroup === "paynow" ? "border-amber-500 bg-amber-500/[0.07]" : "border-line hoverable-strong"}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-bold text-1">Pay Now</span>
                    <span className={`flex h-5 w-5 items-center justify-center rounded-full border-2 ${payGroup === "paynow" ? "border-amber-500" : "border-strong"}`}>
                      {payGroup === "paynow" && <span className="h-2.5 w-2.5 rounded-full bg-amber-500" />}
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-3">UPI · Cards · Net Banking</p>
                </button>

                {user && (
                  <button
                    data-testid="paygroup-credit"
                    onClick={() => creditCtx?.eligible && setPayGroup("credit")}
                    disabled={!creditCtx?.eligible}
                    className={`rounded-xl border p-5 text-left transition-all ${
                      payGroup === "credit" ? "border-amber-500 bg-amber-500/[0.07]" : creditCtx?.eligible ? "border-line hoverable-strong" : "border-line opacity-70"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-bold text-1">PAQVERO Credit</span>
                      <span className={`flex h-5 w-5 items-center justify-center rounded-full border-2 ${payGroup === "credit" ? "border-amber-500" : "border-strong"}`}>
                        {payGroup === "credit" && <span className="h-2.5 w-2.5 rounded-full bg-amber-500" />}
                      </span>
                    </div>
                    <p className="mt-1 text-xs text-3">
                      Pay within {creditCtx?.payment_term_days || 30} days · For approved customers
                    </p>
                  </button>
                )}
              </div>

              {user && creditCtx && !creditCtx.eligible && (
                <div className="mt-3 rounded-xl border border-line bg-hover px-4 py-3.5" data-testid="credit-ineligible-note">
                  {creditCtx.credit_status === "NOT_ELIGIBLE" && (
                    <p className="text-xs text-2">
                      30-day payment terms may be available for approved business customers.{" "}
                      <button onClick={() => navigate("/account/credit/apply")} className="font-bold text-amber-400 hover:text-amber-300" data-testid="credit-apply-link">
                        Apply for PAQVERO Credit →
                      </button>
                    </p>
                  )}
                  {creditCtx.credit_status === "PENDING_REVIEW" && (
                    <p className="text-xs text-2">Your PAQVERO Credit application is under review. We'll notify you once it's decided.</p>
                  )}
                  {creditCtx.credit_status === "SUSPENDED" && (
                    <p className="text-xs text-2">
                      PAQVERO Credit temporarily unavailable. Please clear your outstanding balance to continue using credit.
                    </p>
                  )}
                  {creditCtx.reason === "overdue_block" && (
                    <p className="text-xs text-2">
                      PAQVERO Credit temporarily unavailable — you have an overdue balance of <span className="font-bold text-red-400">{fmt(creditCtx.overdue)}</span>. Please clear it to continue using credit.
                    </p>
                  )}
                  {creditCtx.reason === "exceeds_available" && (
                    <p className="text-xs text-2">
                      {fmt(totals.total - creditCtx.available_credit)} exceeds your available PAQVERO Credit of {fmt(creditCtx.available_credit)}. You can still pay now.
                    </p>
                  )}
                </div>
              )}

              {payGroup === "paynow" && (
                <div className="mt-4 space-y-2.5">
                  {PAYMENT_METHODS.map((m) => (
                    <button
                      key={m.id}
                      data-testid={`payment-${m.id}`}
                      onClick={() => setPaymentMethod(m.id)}
                      className={`flex w-full items-center gap-4 rounded-xl border px-5 py-4 text-left transition-all ${
                        paymentMethod === m.id ? "border-amber-500 bg-amber-500/[0.07]" : "border-line hoverable-strong"
                      }`}
                    >
                      <m.icon className={`h-5 w-5 shrink-0 ${paymentMethod === m.id ? "text-amber-400" : "text-3"}`} />
                      <span className="flex-1">
                        <span className="block text-sm font-bold text-1">{m.label}</span>
                        <span className="block text-xs text-3">{m.desc}</span>
                      </span>
                      <span className={`flex h-5 w-5 items-center justify-center rounded-full border-2 ${paymentMethod === m.id ? "border-amber-500" : "border-strong"}`}>
                        {paymentMethod === m.id && <span className="h-2.5 w-2.5 rounded-full bg-amber-500" />}
                      </span>
                    </button>
                  ))}
                </div>
              )}

              {payGroup === "credit" && creditCtx?.eligible && (
                <div className="mt-4 rounded-xl border border-amber-500/25 bg-amber-500/[0.05] p-5" data-testid="credit-panel">
                  <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-amber-500">PAQVERO Credit</p>
                  <div className="mt-3 space-y-1.5 text-sm">
                    <div className="flex justify-between"><span className="text-2">Payment due</span><span className="font-bold text-1" data-testid="credit-due-date">{fmtDateLong(creditCtx.due_date)}</span></div>
                    <div className="flex justify-between"><span className="text-2">Credit available after this order</span><span className="font-bold text-emerald-400" data-testid="credit-available-after">{fmt(creditCtx.available_after)}</span></div>
                  </div>
                </div>
              )}

              {payGroup === "paynow" && (
                <p className="mt-4 rounded-xl border border-emerald-500/20 bg-emerald-500/[0.05] px-4 py-3 text-xs leading-relaxed text-emerald-300/80">
                  Secure payments by Razorpay (test mode — no real money moves). Card and UPI details
                  are handled entirely by Razorpay and never touch PAQVERO servers.
                </p>
              )}
            </section>
          </div>

          <div className="lg:col-span-2">
            <div className="rounded-2xl border border-line bg-surface p-6 shadow-card lg:sticky lg:top-24" data-testid="checkout-summary">
              <h2 className="font-display text-lg font-medium tracking-tight text-1">Your PAQVERO Order</h2>
              <div className="mt-5 max-h-64 space-y-3.5 overflow-y-auto pr-1" data-lenis-prevent>
                {cart.items.map((item) => (
                  <div key={`${item.product_id}-${item.sku}-${item.channel || "regular"}`} className="flex items-center gap-3">
                    <div className="h-12 w-12 shrink-0 overflow-hidden rounded-lg border border-line bg-white p-1">
                      <img src={item.image} alt="" className="h-full w-full object-contain" loading="lazy" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-xs font-semibold text-1">{item.name}</p>
                      <p className="text-[11px] text-3">{item.variant_label} · {fmtQty(item.qty, item.unit)} × {fmtPerUnit(item.unit_price, item.unit)}
                        {item.prime_priced && <span className="ml-1.5 rounded-full border border-[#E4B66D]/50 bg-[#242521] px-1.5 py-px text-[8px] font-extrabold text-[#E4B66D]" data-testid={`checkout-item-prime-${item.sku}`}>PRIME</span>}
                        {item.member_locked && <span className="ml-1.5 text-[10px] font-bold text-[#A96C1E]" data-testid={`checkout-item-locked-${item.sku}`}>PRIME members only</span>}
                      </p>
                    </div>
                    <span className="text-xs font-bold text-1">{fmt(item.unit_price * item.qty)}</span>
                  </div>
                ))}
              </div>

              <div className="mt-5 border-t border-line pt-4">
                <p className="mb-2 text-xs font-bold uppercase tracking-[0.15em] text-3">Have a coupon?</p>
                {coupon ? (
                  <div className="flex items-center justify-between rounded-xl border border-emerald-500/25 bg-emerald-500/[0.06] px-4 py-3" data-testid="coupon-applied">
                    <span className="flex items-center gap-2 text-sm font-bold text-emerald-400">
                      <Tag className="h-4 w-4" /> {coupon.code} · −{fmt(coupon.discount)}
                    </span>
                    <button data-testid="coupon-remove" onClick={() => { setCoupon(null); setCouponInput(""); }} className="text-xs text-3 hoverable-t1">
                      Remove
                    </button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <input
                      data-testid="coupon-input"
                      value={couponInput}
                      onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                      placeholder="Enter code"
                      className="h-11 flex-1 rounded-xl border border-line bg-input px-4 font-mono text-sm uppercase tracking-wider text-1 placeholder:text-[var(--pq-text3)] focus:border-amber-500/60 focus:outline-none"
                    />
                    <button
                      data-testid="coupon-apply"
                      onClick={applyCoupon}
                      disabled={couponLoading || totals.subtotal <= 0}
                      className="flex items-center rounded-xl border border-amber-500/50 px-5 text-sm font-bold text-amber-400 transition-colors hover:bg-amber-500/10 disabled:opacity-60"
                    >
                      {couponLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Apply"}
                    </button>
                  </div>
                )}
                <p className="mt-2 text-[11px] text-3">Try WELCOME10, FLAT100 or PAQVERO20</p>
              </div>

              <div className="mt-5 space-y-2.5 border-t border-line pt-4 text-sm">
                <div className="flex justify-between"><span className="text-2">Subtotal</span><span className="font-semibold text-1" data-testid="summary-subtotal">{fmt(totals.subtotal)}</span></div>
                {totals.discount > 0 && (
                  <div className="flex justify-between"><span className="text-2">Discount</span><span className="font-semibold text-emerald-400" data-testid="summary-discount">−{fmt(totals.discount)}</span></div>
                )}
                <div className="flex justify-between">
                  <span className="text-2">Delivery{deliveryQuote?.serviceable ? ` · ${deliveryQuote.zone_label}` : ""}</span>
                  <span className="font-semibold text-1" data-testid="summary-shipping">
                    {!deliveryQuote ? <span className="text-3">Enter PIN</span>
                      : !deliveryQuote.serviceable ? <span className="text-red-400">Unavailable</span>
                      : totals.shipping === 0 ? <span className="text-emerald-400">FREE</span>
                      : fmt(totals.shipping)}
                  </span>
                </div>
                {primeBenefit?.eligible && (
                  <div className="flex justify-between" data-testid="prime-discount-line">
                    <span className="font-semibold text-amber-500">PRIME delivery savings</span>
                    <span className="font-semibold text-emerald-400">−{fmt(totals.primeDiscount)}</span>
                  </div>
                )}
                {primeBenefit && !primeBenefit.eligible && primeBenefit.reason && !["not_member"].includes(primeBenefit.reason) && (
                  <p className="text-[11px] text-3" data-testid="prime-ineligible-reason">
                    {primeBenefit.reason === "coupon_stacked" && "PRIME: delivery benefit can't be combined with a coupon."}
                    {primeBenefit.reason === "min_order" && `PRIME: add ${fmt(Math.max(0, primeBenefit.min_order - (totals.subtotal - totals.discount)))} more of merchandise to unlock the delivery benefit.`}
                    {primeBenefit.reason === "cap_reached" && "PRIME: this membership's delivery allowance is fully used."}
                    {primeBenefit.reason === "free_already" && "PRIME: this delivery is already free."}
                    {primeBenefit.reason === "cap_reached" && "PRIME: your delivery-savings cap is fully used for this membership."}
                    {primeBenefit.reason === "max_reached" && "PRIME: you've used all discounted deliveries for this membership."}
                  </p>
                )}
                {gstEnabled && (
                  <div className="flex justify-between"><span className="text-2">GST</span><span className="font-semibold text-1" data-testid="summary-tax">{fmt(totals.tax)}</span></div>
                )}
                <div className="flex justify-between border-t border-line pt-3">
                  <span className="font-bold text-1">Total</span>
                  <span className="font-display text-2xl font-semibold text-amber-500" data-testid="summary-total">{fmt(totals.total)}</span>
                </div>
              </div>

              <ShinyButton
                data-testid="place-order-btn"
                onClick={placeOrder}
                disabled={placing || cart.items.length === 0 || (deliveryQuote && !deliveryQuote.serviceable)}
                className={`mt-6 w-full ${placing || cart.items.length === 0 || (deliveryQuote && !deliveryQuote.serviceable) ? "pointer-events-none opacity-60" : ""}`}
                contentClassName="w-full py-4 text-sm font-bold"
              >
                {placing ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-[18px] w-[18px]" />}
                {placing ? "Placing your order…" : payGroup === "credit" ? "Place Order on Credit →" : paymentMethod === "cod" ? "Place Order (COD)" : `Pay ${fmt(totals.total)}`}
              </ShinyButton>
              <p className="mt-3 text-center text-[11px] text-3">
                Delivery estimate: {deliveryQuote?.serviceable ? (shippingMethod === "standard" ? deliveryQuote.standard.estimate.text : deliveryQuote.express.estimate.text) : "depends on your PIN"} · Tracked end-to-end by PAQVERO
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
